import { useContext } from 'react';

import { isNotDefined } from '@enterprise/core';

import type { PermissionsContentReturnType } from '#context/permissions/types/permission-context-return.type';

import { PermissionsContext } from '#context/permissions/permissions.context';

/**
 * Custom React hook to access the permissions context.
 *
 * This hook retrieves the current permissions context value using React's `useContext`.
 * It ensures that the hook is used within a `PermissionsContext` provider, throwing an error otherwise.
 *
 * @returns {PermissionsContentReturnType} The current permissions context value.
 * @throws {Error} If the hook is used outside of a `PermissionsContext` provider.
 *
 * @example
 * const permissions = usePermissions();
 */
export const usePermissions = (): PermissionsContentReturnType => {
  const context = useContext(PermissionsContext);

  if (isNotDefined(context)) {
    throw new Error('usePermissions must be used within a PermissionsContext');
  }

  return context;
};
